import { Alerts, Communication, Development, Editor, General, Media, Time, Users } from '@poseidon/components/StrokeIcons';
import { isNotEmptyTipTap } from '@poseidon/components/utils';
import { DEFAULT_AWAIT_DEADLINE_DAYS } from '@poseidon/types/types/sequenceOverrideKey';

import { FLOW2_NODES } from '~/components/HelmFlowGraph/flow2.graph';

/**
 * The step palette for a sequence — the step types the /superadmin/flow graph's sequence runner
 * walks (its `StepType`). Kept in sync with `SEQUENCE_STEP_TYPES` on the server
 * (packages/server/src/api/services/pg/pg.sequence.service.ts). A sequence is just an ordered list
 * of these.
 *
 * `call` / `reminder` are manual-task steps: they don't touch LinkedIn/email — the engine creates a
 * rep to-do (a `global_reminder` row, kind `call` / `reminder`) and advances immediately (non-blocking).
 *
 * (Duplicated here rather than imported from the server because the graph spec is not yet a shared
 * package — that extraction is a later part of the epic.)
 */
export const SEQUENCE_STEP_TYPES = [
  'like',
  'follow',
  'wait',
  'connect',
  'await_connect',
  'message',
  'inmail',
  'email',
  'call',
  'reminder',
] as const;
export type SequenceStepType = (typeof SEQUENCE_STEP_TYPES)[number];

/** Container step types — a parallel `group` (fan-out → AND-join), a detached `spawn`, or a `switch`
 * (route by a numeric attribute into ONE branch). Authored via the palette's "Parallel" section, kept
 * separate from the touch steps. Mirrors the server. */
export const SEQUENCE_CONTAINER_STEP_TYPES = ['group', 'spawn', 'switch'] as const;
export type SequenceContainerStepType = (typeof SEQUENCE_CONTAINER_STEP_TYPES)[number];

/** The numeric attributes a `switch` can route on — mirrors the server SWITCH_ATTRIBUTES registry. */
export type SequenceSwitchAttribute = 'age' | 'aum';
export const SEQUENCE_SWITCH_ATTRIBUTES: Record<SequenceSwitchAttribute, { label: string; unit: string }> = {
  age: { label: 'Age', unit: 'years' },
  aum: { label: '~AUM', unit: 'USD' },
};
export const DEFAULT_SWITCH_ATTRIBUTE: SequenceSwitchAttribute = 'aum';

/** Fixed pixel pitch of one route-branch column in the switch's below-node fan. Shared so the graph
 *  editor (block position + width reservation + branch-step editor anchor) and the fan's own connector
 *  SVG (stem → distributor → per-column drops) stay pixel-aligned. Wide enough to hold a branch's
 *  editable `wait` pill (clock + number + days/weeks toggle) without it overflowing into the next column. */
export const SWITCH_FAN_COL_W = 224;

/** Distance from a spawn node's right edge to the FIRST (innermost) flaired arm's center — wide enough to
 *  clear the branch's own wait pills before the arm starts. */
export const SPAWN_ARM_BASE = 220;
/** Horizontal pitch between adjacent flaired-arm lanes (arm box + gap). Each spawn in a branch gets its own
 *  lane to the right; the TOP spawn takes the OUTERMOST lane so its connector crosses only empty space.
 *  Shared by the branch line (per-spawn lane offset) and the switch fan (right-margin reservation). */
export const SPAWN_LANE_W = 260;

/**
 * One branch of a `switch`: a half-open numeric range `[minValue, maxValue)` on the switch's attribute
 * (null = unbounded that side), routing a matching contact into `arm` (a terminal sub-sequence — no
 * rejoin). `isCatchAll` is the "None" fallback for a missing/unmatched value.
 */
export interface SequenceSwitchBranch {
  minValue: number | null;
  maxValue: number | null;
  label: string | null;
  isCatchAll: boolean;
  arm: SequenceStep[];
}

/** Auto-managed step types that appear in a stored sequence but are NOT pickable (withdraw is
 * auto-inserted at the head of a not-accepted branch). */
export type SequenceAutoStepType = 'withdraw';
export type SequenceAnyStepType = SequenceStepType | SequenceContainerStepType | SequenceAutoStepType;

/** Per-outcome branch keys (only `notAccepted`, off connect/await, for now). */
export type SequenceOutcome = 'notAccepted';

/** Minimum arms a parallel `group` must have to be meaningful (fewer → use plain steps). */
export const MIN_GROUP_ARMS = 2;

/**
 * One step in a sequence. `waitDays` applies to 'wait', `deadlineDays` to 'await_connect'.
 * `branches.notAccepted` is the sub-sequence run when a connection isn't accepted. Container steps
 * carry parallel sub-sequences — `arms` (group, AND-join) and `arm` (spawn, detached). Container
 * nesting is capped at depth 1 (an arm holds only plain steps).
 */
export interface SequenceStep {
  type: SequenceAnyStepType;
  /** type: 'wait' — the wait length, ALWAYS in canonical days (what the engine waits). */
  waitDays?: number;
  /** type: 'wait' — how `waitDays` is displayed/entered ('weeks' = waitDays/7). Default 'days'. */
  waitUnit?: 'days' | 'weeks';
  deadlineDays?: number;
  /** Editable content: message / connect note / inmail / email body. */
  body?: string;
  /** Editable content: inmail / email subject line. */
  subject?: string;
  /**
   * type: 'email' — continue the previous email thread (reuse its subject + send in-thread) rather than
   * starting a new one. Default true (an email following an earlier email threads by default); an explicit
   * `false` opts into a fresh thread with the step's own subject. See {@link isEmailThreadContinued}.
   */
  replyToPrevious?: boolean;
  /** Editable content: how many posts a `like` step likes. */
  likeCount?: number;
  /** This step's content is AI-personalized per contact (drives `seq.draft` → `human.review_drafts`).
   *  Mirrors the server `SequenceStep.isPersonalized` (pg.sequence.service.ts). */
  isPersonalized?: boolean;
  branches?: Partial<Record<SequenceOutcome, SequenceStep[]>>;
  /** type: 'group' — N parallel arms; advances past the group only when ALL complete. */
  arms?: SequenceStep[][];
  /** Reserved join policy. Only 'all' (AND-barrier) for now. */
  join?: 'all';
  /** type: 'spawn' — one detached arm run alongside the main line (which continues; no rejoin). */
  arm?: SequenceStep[];
  /** type: 'switch' — route the contact by a numeric attribute (age/AUM) into ONE branch (terminal). */
  switch?: { attribute: SequenceSwitchAttribute; branches: SequenceSwitchBranch[] };
}

/** Default wait length applied to a newly-added 'wait' step. */
export const DEFAULT_WAIT_DAYS = 1;
/** Default number of posts a newly-added 'like' step likes. */
export const DEFAULT_LIKE_COUNT = 1;
/**
 * Default acceptance deadline applied to a newly-added 'await_connect' step. Re-exported from
 * `@poseidon/types` rather than redeclared: the ENGINE parks the contact for exactly this many days, so a
 * local literal that drifted would make the pill promise a window the engine doesn't honour.
 */
export const DEFAULT_DEADLINE_DAYS = DEFAULT_AWAIT_DEADLINE_DAYS;

/** Graph node id each step type maps to (matches the /superadmin/flow runner) — used to place faces. */
export const STEP_NODE_ID: Record<SequenceAnyStepType, string> = {
  like: 'seq.step.like',
  follow: 'seq.step.follow',
  wait: 'seq.wait',
  connect: 'seq.step.connect',
  await_connect: 'seq.await_connect',
  message: 'seq.step.message',
  inmail: 'seq.step.inmail',
  email: 'seq.step.email',
  call: 'seq.step.call',
  reminder: 'seq.step.reminder',
  withdraw: 'seq.withdraw',
  group: 'seq.fork',
  spawn: 'seq.spawn',
  switch: 'seq.switch',
};

/**
 * Graph node ids each pre-step folds up — used to sum faces sitting anywhere in that stage. These MUST
 * match the FLOW_JOURNEY node ids the engine writes to `flow.enrollment.current_node_id`
 * (`@poseidon/types/types/journey`), NOT the diagram labels — the runner and the diagram share that spec,
 * and a mismatch here silently hides enrolled people (they sit on a node no pre-step claims). The park
 * lanes are folded into their originating stage so a stuck person still shows up (they're still enrolled).
 */
export const PRE_STEP_NODE_IDS: Record<string, string[]> = {
  research: [
    'seq.add', // manual entry — just added, about to research
    'status.adding',
    'research.should',
    'unipile.enabled',
    'unipile.slot',
    'status.researching',
    'status.waiting_slot',
    'unipile.fetch',
    'unipile.slot.release',
    'title.rescreen',
    'tavily.slot',
    'status.waiting_tavily',
    'tavily.search',
    'tavily.slot.release',
    'status.gathering_sources',
    // Parked / errored / awaiting-a-human mid-research but still enrolled (auto-retrying or awaiting a
    // fix) — surface them here rather than letting them sit on a node no pre-step claims (which would
    // silently hide an enrolled contact). Covers the blocked pills AND the human-action / dead side-lanes.
    'unipile.disconnected',
    'status.rate_limited',
    'status.needs_salesnav',
    'status.set_aside',
    'status.rate_limited_tavily',
    'status.web_search_failed',
    'human.review_identity',
    'action.reconnect_linkedin',
    'action.add_salesnav',
    'alert.unipile_misconfigured',
    'contact.delete',
    'contact.purge', // the brief active hop while the hard-delete runs (before the contact + its flow rows vanish)
  ],
  // Dossier fold includes its own failure lane so a synthesis-failed contact still shows in this stage.
  dossier: ['dossier.synthesize', 'status.synthesize_failed'],
  // Qualify fold includes the disqualified terminal so a screened-out contact still shows in this stage,
  // and the model-error retry park (status.waiting_qualify, POS-2407) so a parked contact isn't hidden.
  qualify: ['qualify.rules', 'qualify.icp', 'status.waiting_qualify', 'status.pending_review', 'status.disqualified'],
  enroll: ['status.enrolled', 'seq.run_step'],
};

/**
 * Extra FLOW_JOURNEY nodes a contact can be PARKED on *while a given authored step is the active step* —
 * states the engine routes into after dispatching the step but BEFORE the cursor advances to the next
 * one. A contact sitting on one of these belongs to that step, so the editor must pin its face onto the
 * step node — not only the step's own `seq.step.*` node — or the parked contact silently vanishes from
 * the authored graph while the full-journey graph still shows them (the "Waiting on reminder" gap).
 *
 * Only lanes reached EXCLUSIVELY from ONE step type are listed here, so a face is never attributed to
 * two different steps at once:
 *  - `call` / `reminder` create a rep to-do and park until a human logs it (they NEVER auto-advance), so
 *    the parked contact rests on the matching status node — the exact people this map must surface.
 *  - `connect` is the only step that can hit LinkedIn's WEEKLY invite cap; `inmail` the only one that can
 *    run out of Sales-Nav credits — each parks on its own blocked lane until the weekly reset.
 *
 * Lanes shared by every touch send (validate / working-hours / blocked-send / reconnect-LinkedIn /
 * unresolved-variables / missing-content / needs-attention) are deliberately NOT attributed to a step —
 * they can't be tied to one authored step by node id alone, so the editor folds them into its single
 * "Other states" pile instead (see SequenceGraphEditor).
 */
export const STEP_WAIT_NODE_IDS: Partial<Record<SequenceAnyStepType, string[]>> = {
  call: ['status.call_waiting'],
  reminder: ['status.reminder_waiting'],
  connect: ['blocked.invite_cap'],
  inmail: ['blocked.inmail_credits'],
};

/** The manual-task park lanes a `call` / `reminder` step rests a contact on until a human logs the to-do
 * done — derived from {@link STEP_WAIT_NODE_IDS} so the node ids can't drift from the step→lane map. Mirrors
 * the engine's `MANUAL_TASK_WAIT_NODES` + the server's `SEQUENCE_MANUAL_TASK_WAIT_NODES`. A face here can be
 * marked done straight from the graph (see {@link isManualTaskWaitNode}). */
export const MANUAL_TASK_WAIT_NODE_IDS: string[] = [
  ...(STEP_WAIT_NODE_IDS.call ?? []),
  ...(STEP_WAIT_NODE_IDS.reminder ?? []),
];

/** Whether a contact resting on this node is parked on a call/reminder to-do a human can mark done — the
 * one state where the graph offers a "Mark done" control (it advances the arm past the manual step). */
export function isManualTaskWaitNode(nodeId: string): boolean {
  return MANUAL_TASK_WAIT_NODE_IDS.some((id) => id === nodeId);
}

/** The manual-task park lane a `call` / `reminder` step rests a contact on — the node id `sequence.completeTask`
 * expects when a human marks that step done from the locked per-contact graph. Null for every other step type
 * (nothing to complete by hand). Derived from {@link STEP_WAIT_NODE_IDS} so it can't drift from the step→lane map. */
export function manualTaskWaitNodeForStepType(type: SequenceAnyStepType): string | null {
  return STEP_WAIT_NODE_IDS[type]?.find((id) => isManualTaskWaitNode(id)) ?? null;
}

/**
 * Every FLOW_JOURNEY node a contact currently ON this step can sit on: the step's own node plus any
 * park/wait lanes it can be routed into before advancing ({@link STEP_WAIT_NODE_IDS}). The editor sums
 * faces across all of them so a parked contact shows on the step it's parked at, ready to action.
 */
export function stepFaceNodeIds(type: SequenceAnyStepType): string[] {
  const wait = STEP_WAIT_NODE_IDS[type];
  return wait == null ? [STEP_NODE_ID[type]] : [STEP_NODE_ID[type], ...wait];
}

/**
 * Node id → short human status, reused from the /superadmin/flow diagram titles (the display copy for the
 * same FLOW_JOURNEY spec, kept in lockstep by flow2.parity). Lets a rep hover an enrolled face and read the
 * exact stage that person is parked on (e.g. "Researching", "Hit LinkedIn limit", "Send message").
 */
const NODE_STATUS_LABEL: Record<string, string> = Object.fromEntries(FLOW2_NODES.map((n) => [n.id, n.title]));

/** A readable status for the node a person currently sits on; falls back to the raw id if unmapped. */
export function nodeStatusLabel(nodeId: string): string {
  return NODE_STATUS_LABEL[nodeId] ?? nodeId;
}

const NODE_KIND: Record<string, string> = Object.fromEntries(FLOW2_NODES.map((n) => [n.id, n.kind]));

/** How to flag a contact's avatar on the graph based on the node they sit on. Mirrors the diagram's own
 * tones: `dead` → error (red), `blocked` → stuck-but-retrying (amber). Everything else is un-flagged. */
export type FaceTone = 'error' | 'blocked';

/**
 * The alert tone for a person on this node, or null for a normal (in-flight / done / human) state:
 * - `error` — the `dead` terminal-negative kind (disqualified / dead-letter / terminal.errored /
 *   synthesize-failed); matches `HELM_KIND_TONE.dead = 'red'` and the engine's `enrollment.status = 'errored'`.
 * - `blocked` — the `blocked` kind (disconnected / rate-limited / set-aside / …): stuck, but auto-retrying.
 */
export function faceTone(nodeId: string): FaceTone | null {
  const kind = NODE_KIND[nodeId];
  if (kind === 'dead') return 'error';
  if (kind === 'blocked') return 'blocked';
  return null;
}

/**
 * Whether a contact resting on this node is waiting on a PERSON — a call/reminder to-do, a draft review, a
 * missing-content or reconnect gate. Mirrors the diagram's `human` kind, and drives the locked graph's
 * orange "needs you" tone (distinct from `blocked`, which retries by itself).
 */
export function nodeNeedsHuman(nodeId: string): boolean {
  return NODE_KIND[nodeId] === 'human';
}

/**
 * How to flag a PERSON resting on this node — {@link faceTone}'s diagram tones plus the `human` "needs you"
 * state that {@link faceTone} deliberately omits (it maps the diagram's node tints, where `human` has none).
 *
 * ONE definition, because a person parked on a human gate is stuck-until-someone-acts and every rep-facing
 * surface has to say so: the People list pill, its sort order, and the locked per-contact graph all read
 * this. They used to derive it separately, and the People list simply forgot the `human` case — so a contact
 * blocked on an unresolved merge field rendered with the same neutral pill as one healthily mid-flight.
 *
 * KNOWN GAP (not closed by this helper): the People list feeds it `flow.enrollment.current_node_id`, which
 * the engine mirrors only from the ROOT arm. A contact whose SPAWN or GROUP arm is parked on a human gate
 * while the root sits on a wait therefore still reads as a normal wait here — the Needs Review queue,
 * which reads per-arm, is the surface that catches those.
 *
 * NOT used for the graph's face RINGS ({@link faceTone} still is): a `call` / `reminder` step parks on a
 * human node as NORMAL operation, so ringing every human node would put a permanent alert on healthy
 * sequences. Those faces get their own "Mark done" affordance instead (see {@link isManualTaskWaitNode}).
 */
export type PersonTone = FaceTone | 'needsYou';

export function personTone(nodeId: string): PersonTone | null {
  return faceTone(nodeId) ?? (nodeNeedsHuman(nodeId) ? 'needsYou' : null);
}

/**
 * Whether a journey node is a terminal SUCCESS state (`enrolled` / `finished` / `responded`). The
 * editor's "Other states" catch-all skips these so a long-running sequence's completed contacts don't
 * pile up there — they're a finished outcome, not something to action on the authored graph.
 */
export function isJourneyDoneNode(nodeId: string): boolean {
  return NODE_KIND[nodeId] === 'done';
}

/**
 * The graph node a contact rests on once it runs off the end of the steps — the terminal the engine's
 * `finishArm` routes to on the runner's "no more steps" edge. The editor draws it as the closing node of
 * the spine so completed contacts have somewhere to land; without it their faces belong to no node and the
 * done-node skip above hides them from the canvas entirely.
 */
export const SEQUENCE_FINISHED_NODE_IDS: string[] = ['terminal.finished'];

/** A subject (TipTap HTML) that has real content, or null (blank / `<p></p>` → nothing to inherit). */
function threadSubjectOrNull(subject: string | undefined): string | null {
  return subject != null && isNotEmptyTipTap(subject) ? subject : null;
}

/**
 * The subject of the nearest earlier email step on this path (the same `steps` array — spine or arm)
 * before `index`, or null when this is the first email. Mirrors the campaign's `findPreviousEmailSubject`.
 * Drives the email step's "continue previous thread" toggle: it only shows when an earlier email exists,
 * and the returned subject is what a thread-continuing step inherits (a blank prior subject → null,
 * nothing to inherit). Also looks inside a preceding parallel group's arms (a grouped email counts).
 */
export function previousEmailSubject(steps: SequenceStep[], index: number): string | null {
  for (let i = index - 1; i >= 0; i--) {
    const s = steps[i];
    if (s == null) continue;
    if (s.type === 'email') return threadSubjectOrNull(s.subject);
    if (s.type === 'group') {
      const armEmail = (s.arms ?? []).flat().find((a) => a.type === 'email');
      if (armEmail != null) return threadSubjectOrNull(armEmail.subject);
    }
  }
  return null;
}

/**
 * Whether the email step at `index` continues the previous email thread — an `email` whose
 * `replyToPrevious` is on (the default) AND that has an earlier email to thread onto. Such a step inherits
 * the prior thread's subject (the subject field is hidden in the editor) and needs no subject of its own.
 */
export function isEmailThreadContinued(steps: SequenceStep[], index: number): boolean {
  const step = steps[index];
  if (step == null || step.type !== 'email') return false;
  return (step.replyToPrevious ?? true) && previousEmailSubject(steps, index) != null;
}

/** Severity of a step problem: `error` blocks (red ring), `warning` is advisory (amber ring). */
export type StepIssueLevel = 'error' | 'warning';
export interface StepIssue {
  level: StepIssueLevel;
  message: string;
}

/** Wrap a plain content-issue message (always a hard error) as a {@link StepIssue}, or null. */
export function toErrorIssue(message: string | null): StepIssue | null {
  return message == null ? null : { level: 'error', message };
}

/** The chip ring class for a step's issue — red for an error, amber for an advisory warning, none if fine. */
export function stepIssueRingClass(issue: StepIssue | null | undefined): string | false {
  if (issue == null) return false;
  return issue.level === 'warning' ? 'ring-2 ring-amber-400' : 'ring-2 ring-red-400';
}

/**
 * A problem with the step at `index`, or null if it's fine. Errors (red) block; warnings (amber) are just
 * advisory. Right now the only warning is "Await accept" with no Connect before it — it's *recommended* but
 * not required. A new-thread email still needs its own subject (a thread-continuing email inherits the
 * previous one — see {@link isEmailThreadContinued}), and the container shapes are hard errors.
 */
export function sequenceStepIssue(steps: SequenceStep[], index: number): StepIssue | null {
  const step = steps[index];
  if (step == null) return null;
  const content = stepContentIssue(step);
  if (content != null) return { level: 'error', message: content };
  // Email subject is contextual: required only when this email starts a NEW thread (no earlier email, or
  // the rep turned threading off). A thread-continuing email inherits the prior subject, so none is needed.
  if (step.type === 'email' && !isEmailThreadContinued(steps, index) && !isNotEmptyTipTap(step.subject)) {
    return { level: 'error', message: 'Add a subject — this email starts a new thread.' };
  }
  // A Connect can sit directly on the line OR inside a preceding parallel group (connect + like fire
  // together), so look inside group arms too — else a grouped connect would wrongly trip this.
  const hasConnectBefore = steps
    .slice(0, index)
    .some((s) => s.type === 'connect' || (s.type === 'group' && (s.arms ?? []).some((arm) => arm.some((a) => a.type === 'connect'))));
  if (step.type === 'await_connect' && !hasConnectBefore) {
    // Advisory, not blocking: "Await accept" waits for a connection request to be accepted, so it works best
    // right after a Connect — but it's allowed without one (e.g. the invite was sent elsewhere).
    return {
      level: 'warning',
      message: 'Not required, but recommended: add a Connect step before “Await accept” — it waits for that connection request to be accepted.',
    };
  }
  if (step.type === 'group') {
    const arms = step.arms ?? [];
    if (arms.length < MIN_GROUP_ARMS)
      return { level: 'error', message: 'A parallel group needs at least 2 arms — otherwise use plain steps.' };
    if (arms.some((a) => a.length === 0)) return { level: 'error', message: 'A parallel arm can’t be empty.' };
    // A group fans out one-step arms and waits for all to finish — each arm is exactly one step deep.
    if (arms.some((a) => a.length > 1)) return { level: 'error', message: 'A parallel arm can only be a single step.' };
  }
  if (step.type === 'spawn' && (step.arm ?? []).length === 0) {
    return { level: 'error', message: 'Add at least one step to the spawned branch.' };
  }
  if (step.type === 'switch') {
    // A range alone is a complete branch — steps inside a branch are OPTIONAL (a Switch can just segment
    // contacts by range), so we do NOT require any branch to carry a step.
    // The Switch routes into ONE branch and ends the main line (each branch is its own terminal path),
    // so any step sitting after it on the spine can never run — flag it so it's moved into a branch.
    if (index < steps.length - 1) {
      return { level: 'error', message: 'A Switch ends the main line — steps after it never run. Put them inside a branch instead.' };
    }
  }
  return null;
}

/**
 * Why a step type can't be inserted at `index` (null = allowed). The insert gate only blocks HARD errors;
 * advisory issues (e.g. "Await accept" reads best after a Connect) are surfaced by {@link sequenceStepIssue}
 * as amber warnings AFTER the step is added, not blocked here — an "Await accept" without a preceding Connect
 * is allowed (the invite may have been sent elsewhere). No step type is hard-blocked at insert time today.
 */
export function insertDisabledReason(_steps: SequenceStep[], _index: number, _type: SequenceStepType): string | null {
  return null;
}

/**
 * A step that can be drag-merged with another into a parallel group — a plain action step. NOT a
 * `wait` (waiting in parallel is meaningless), NOT a container (`group`/`spawn` — parallels don't
 * nest), and not the auto `withdraw`. Drives the graph editor's drop-onto-node → parallel gesture.
 */
export function canMergeIntoGroup(step: SequenceStep | null | undefined): boolean {
  return (
    step != null &&
    step.type !== 'wait' &&
    step.type !== 'group' &&
    step.type !== 'spawn' &&
    step.type !== 'switch' &&
    step.type !== 'withdraw'
  );
}

/**
 * A freshly-authored step of `type` with its per-type creation defaults: a `wait` gets
 * {@link DEFAULT_WAIT_DAYS}, an `await_connect` gets {@link DEFAULT_DEADLINE_DAYS} (its acceptance
 * deadline). Every insertion surface (spine + arms) goes through this so the defaults can't drift.
 */
export function newSequenceStep(type: SequenceStepType): SequenceStep {
  if (type === 'wait') return { type, waitDays: DEFAULT_WAIT_DAYS };
  if (type === 'await_connect') return { type, deadlineDays: DEFAULT_DEADLINE_DAYS };
  return { type };
}

/** A fresh, empty `switch` route branch — an unbounded range (or the catch-all) with no steps yet. */
export function newSwitchBranch(isCatchAll = false): SequenceSwitchBranch {
  return { minValue: null, maxValue: null, label: null, isCatchAll, arm: [] };
}

/** A freshly-authored `switch` step: routes by ~AUM, seeded with one empty range branch to fill in. */
export function newSwitchStep(): SequenceStep {
  return { type: 'switch', switch: { attribute: DEFAULT_SWITCH_ATTRIBUTE, branches: [newSwitchBranch()] } };
}

/** A freshly-inserted parallel container: an empty two-arm `group`, a one-arm `spawn`, or a `switch`. */
export function newContainerStep(type: SequenceContainerStepType): SequenceStep {
  if (type === 'group') return { type: 'group', arms: [[], []], join: 'all' };
  if (type === 'spawn') return { type: 'spawn', arm: [] };
  return newSwitchStep();
}

/** Drop trailing `.0` from a rounded ratio (1.0 → "1", 1.5 → "1.5"). */
function trimNum(n: number): string {
  return Number(n.toFixed(1)).toString();
}

/** A compact human display for a numeric switch bound: age → "50"; ~AUM → "$500K" / "$1.5M" / "$2B". */
export function formatSwitchValue(attribute: SequenceSwitchAttribute, value: number): string {
  if (attribute === 'age') return String(value);
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${trimNum(value / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `$${trimNum(value / 1_000_000)}M`;
  if (abs >= 1_000) return `$${trimNum(value / 1_000)}K`;
  return `$${value}`;
}

/** The range chip text for a switch branch: "< $1M" · "$1M–$10M" · "≥ $10M" · "Any" · "Doesn’t match any range"
 *  (the catch-all fallback). */
export function switchBranchLabel(attribute: SequenceSwitchAttribute, branch: SequenceSwitchBranch): string {
  if (branch.isCatchAll) return 'Doesn’t match any range';
  const lo = branch.minValue;
  const hi = branch.maxValue;
  if (lo != null && hi != null) return `${formatSwitchValue(attribute, lo)}–${formatSwitchValue(attribute, hi)}`;
  if (lo != null) return `≥ ${formatSwitchValue(attribute, lo)}`;
  if (hi != null) return `< ${formatSwitchValue(attribute, hi)}`;
  return 'Any';
}

/** The exact stored bounds as editable strings ('' = that side is unbounded), one per field of the
 *  two-input range editor. Seeded from the EXACT stored values — NOT the rounded chip display — so opening
 *  a range and committing it unchanged can't silently round a bound (e.g. 1_250_000 shows as "$1.3M" but
 *  must edit back to 1_250_000, not 1_300_000). Each side round-trips through {@link parseSwitchBound}. */
export function switchRangeEditParts(branch: SequenceSwitchBranch): { min: string; max: string } {
  return {
    min: branch.minValue == null ? '' : String(branch.minValue),
    max: branch.maxValue == null ? '' : String(branch.maxValue),
  };
}

/** Parse one bound a user typed into a range field. Tolerant of `$`, commas, whitespace and k/m/b
 *  suffixes ("$1.5M" → 1500000, "100k" → 100000). Blank → null (unbounded); unparseable → null. */
export function parseSwitchBound(raw: string): number | null {
  const t = raw.replace(/[$,\s]/g, '');
  if (t === '') return null;
  const m = /^(\d*\.?\d+)([kmb])?$/i.exec(t);
  if (m == null) return null;
  const base = Number(m[1]);
  if (!Number.isFinite(base)) return null;
  const suffix = m[2]?.toLowerCase();
  const mult = suffix === 'k' ? 1_000 : suffix === 'm' ? 1_000_000 : suffix === 'b' ? 1_000_000_000 : 1;
  return base * mult;
}

/**
 * Build the new spine after inserting a step of `type` at gap `index` (0..length). Applies per-type
 * creation defaults (see {@link newSequenceStep}) and auto-follow-ups: a `connect` is trailed by an
 * `await_connect` ("Await accept") — you almost always wait for the invite to be accepted before the next
 * touch — UNLESS an `await_connect` already immediately follows, so a connect dropped just before an
 * existing one doesn't double up. Pure; the editor wraps this with its picker/editor side-effects.
 */
export function insertSpineStep(steps: SequenceStep[], index: number, type: SequenceStepType): SequenceStep[] {
  const next = steps.slice();
  next.splice(index, 0, newSequenceStep(type));
  if (type === 'connect' && next[index + 1]?.type !== 'await_connect') {
    next.splice(index + 1, 0, newSequenceStep('await_connect'));
  }
  return next;
}

/**
 * A step that can RECEIVE a drag-merge: a basic step (→ a new parallel group of the two) or an
 * existing `group` (→ the dragged step joins as a new arm). Not a `wait` and not a `spawn`.
 */
export function canReceiveMerge(step: SequenceStep | null | undefined): boolean {
  return step != null && (step.type === 'group' || canMergeIntoGroup(step));
}

/** Human label for a step, including the wait duration for 'wait' and the count for 'like'. */
export function stepLabel(step: SequenceStep): string {
  if (step.type === 'wait') {
    const days = step.waitDays ?? DEFAULT_WAIT_DAYS;
    if (step.waitUnit === 'weeks') {
      const weeks = Math.max(1, Math.round(days / 7));
      return `Wait ${weeks} week${weeks === 1 ? '' : 's'}`;
    }
    return `Wait ${days} day${days === 1 ? '' : 's'}`;
  }
  if (step.type === 'like') {
    const n = step.likeCount ?? DEFAULT_LIKE_COUNT;
    return `Like ${n} post${n === 1 ? '' : 's'}`;
  }
  if (step.type === 'await_connect') {
    const days = step.deadlineDays ?? DEFAULT_DEADLINE_DAYS;
    return `Await accept · up to ${days} day${days === 1 ? '' : 's'}`;
  }
  return STEP_META[step.type].label;
}

/**
 * Which content fields a step type exposes for editing (null → nothing editable via the step editor).
 * `body: 'optional'` renders the field but does NOT require it (used by the call / reminder note — the
 * validators below gate on `=== true`, so an optional field never blocks save).
 */
export interface StepEditSpec {
  body?: boolean | 'optional';
  subject?: boolean;
  likeCount?: boolean;
}
export function stepEditSpec(type: SequenceAnyStepType): StepEditSpec | null {
  switch (type) {
    case 'message':
      return { body: true };
    case 'connect':
      return { body: 'optional' }; // a connection note is optional — LinkedIn allows a bare invite (empty note)
    case 'inmail':
    case 'email':
      return { subject: true, body: true };
    case 'like':
      return { likeCount: true };
    case 'call':
      return { body: 'optional' }; // an optional note the rep sees on the to-do
    case 'reminder':
      return { body: true }; // the chosen reminder action (its name lives on `body`) is required — a reminder must have one set
    default:
      return null; // follow / wait (its own inline duration) / await_connect / containers
  }
}
/** Whether the step has content the step editor can edit. */
export function stepIsEditable(step: SequenceStep): boolean {
  return stepEditSpec(step.type) != null;
}

/**
 * Whether a step type requires the user to provide content before it's valid — a body and/or subject
 * to type (message / inmail / email), or a reminder action to pick.
 */
export function stepRequiresContent(type: SequenceAnyStepType): boolean {
  const spec = stepEditSpec(type);
  return spec != null && (spec.body === true || spec.subject === true);
}

/**
 * Whether inserting a step of this type should immediately open its content editor — the steps a rep
 * almost always writes into: the required-content steps (message / inmail / email / reminder — the
 * reminder needs an action picked) plus `connect` (whose note is optional but commonly added).
 * Deliberately excludes `call`, whose note is optional and usually skipped, so adding one doesn't
 * steal focus. Shared by every insertion surface
 * (spine + parallel arms) so the behaviour can't drift between them.
 */
export function stepOpensEditorOnInsert(type: SequenceAnyStepType): boolean {
  return stepRequiresContent(type) || type === 'connect';
}

/**
 * A required-content problem for a step whose body / subject must be filled in (message, inmail,
 * email) — or, for a reminder, whose action must be chosen — else null. Treats an empty TipTap
 * document (`<p></p>`) as blank. Steps with only optional content (connect's note, call's note) and
 * `like` (a default count) are exempt.
 */
export function stepContentIssue(step: SequenceStep): string | null {
  const spec = stepEditSpec(step.type);
  if (spec == null) return null;
  // A reminder's `body` holds the chosen action's name (picked from the dropdown, not typed) — it's
  // required, so a reminder with none set is flagged (parity with a message that has no body).
  if (step.type === 'reminder') return isNotEmptyTipTap(step.body) ? null : 'Pick a reminder action.';
  // Email's subject is contextual (a threaded reply inherits it), so it's validated by sequenceStepIssue,
  // which knows the surrounding steps. inmail's subject is always its own → still required here.
  if (spec.subject === true && step.type !== 'email' && !isNotEmptyTipTap(step.subject)) return 'Add a subject.';
  if (spec.body === true && !isNotEmptyTipTap(step.body)) return 'Add the message content.';
  return null;
}

/**
 * The automatic front-half of the journey that runs before your outreach steps — the gist of what
 * the engine does to every enrolled person (from the /superadmin/flow graph). Read-only context,
 * not editable steps.
 */
export interface SequenceSubStep {
  label: string;
  gist: string;
}

export interface SequencePreStep {
  id: string;
  label: string;
  gist: string;
  icon: React.JSX.Element;
  /** The graph sub-steps this pre-step folds up — revealed when expanded. */
  subSteps: SequenceSubStep[];
}

export const SEQUENCE_PRE_STEPS: SequencePreStep[] = [
  {
    id: 'research',
    label: 'Research',
    gist: 'Fetch the person’s profile from the LinkedIn API.',
    icon: <General.SearchSm />,
    subSteps: [
      {
        label: 'Should query?',
        gist: 'Recency gate on when we last queried the LinkedIn API — never / stale (> 1 day) → fetch; fresh (< 1 day) → skip the fetch, straight to the dossier.',
      },
      {
        label: 'LinkedIn API connected?',
        gist: 'Checks the LinkedIn API flag + a connected LinkedIn account; not connected parks the contact and prompts a reconnect.',
      },
      {
        label: 'Acquire LinkedIn API slot',
        gist: '1 of 5 Redis slots per (user, team) — shared with sends, so research and outreach throttle together. All busy → parks, and a cron retries.',
      },
      {
        label: 'Fetch profile',
        gist: 'Pulls the LinkedIn profile via the LinkedIn API, then releases the slot and routes on the outcome — no profile → delete, ambiguous → identity review, rate-limited / disconnected / no Sales Nav → parks.',
      },
    ],
  },
  {
    id: 'dossier',
    label: 'Dossier',
    gist: 'Search the web (Tavily), then AI writes a structured research dossier.',
    icon: <Editor.Wand />,
    subSteps: [
      {
        label: 'Acquire Tavily slot',
        gist: '1 of 5 Redis slots per (user, team) before the web search. All busy → parks, and a cron retries.',
      },
      {
        label: 'Search the web',
        gist: 'Tavily gathers public sources (company, role, recent signals). A search failure is recorded but non-fatal — the dossier still builds from the profile alone.',
      },
      {
        label: 'Synthesize dossier',
        gist: 'Claude reads the profile + web results and writes the dossier (background, career highlights, recent + wealth signals) — the facts qualification reads.',
      },
    ],
  },
  {
    id: 'qualify',
    label: 'Qualify',
    gist: 'Right person? The dossier is screened against your ICP before any outreach.',
    icon: <General.FilterLines />,
    subSteps: [
      {
        label: 'Filter rules',
        gist: 'Deterministic, rep-configured rules over the profile + dossier. Free, no AI, runs for every candidate. Fail → disqualified.',
      },
      {
        label: 'ICP qualify',
        gist: 'AI verdict on the dossier vs your audience / avoid (when the audience filter is on). Yes → enroll · no → disqualified · unsure → parked for a rep to review.',
      },
    ],
  },
  {
    id: 'enroll',
    label: 'Enroll',
    gist: 'Passes qualification → the contact is enrolled and the sequence begins.',
    icon: <Users.UserPlus01 />,
    subSteps: [
      {
        label: 'Enroll in sequence',
        gist: 'The qualified contact is bound to the sequence and its status becomes “enrolled”.',
      },
      {
        label: 'Run steps',
        gist: 'The step driver reads your steps in order and dispatches each by type (connect, message, wait, …) — the editable steps below start running.',
      },
    ],
  },
];

/**
 * The four automatic stages folded into ONE node — what the LOCKED per-contact graph draws in place of the
 * builder's stage-by-stage column. On a contact's own page the front-half isn't a pipeline to reason about:
 * it's one thing that either already happened to this person or is happening to them right now. So it
 * collapses to a single "Prep" node whose expanded detail is the four stages (each stage's own sub-steps
 * stay in the builder, where a rep is designing the pipeline rather than reading one person's progress).
 */
export const SEQUENCE_PREP_STEP: SequencePreStep = {
  id: 'prep',
  label: 'Prep',
  gist: 'Researched, written up and screened against your ICP — automatically, before the first touch.',
  icon: <Editor.Wand />,
  subSteps: SEQUENCE_PRE_STEPS.map((stage) => ({ label: stage.label, gist: stage.gist })),
};

/**
 * Every journey node the folded {@link SEQUENCE_PREP_STEP} stands for: a contact resting on one of these
 * has NOT reached an authored step yet, so the locked graph marks Prep as where they are.
 *
 * Deliberately NOT the plain union of {@link PRE_STEP_NODE_IDS}: the enroll fold also claims `seq.run_step`
 * (the step driver's own hop) so the builder's Enroll pile catches someone mid-dispatch. Here that node
 * means the OPPOSITE — the contact is off in the authored steps — so counting it would read every
 * mid-sequence contact as still in prep.
 */
export const PREP_NODE_IDS: string[] = [
  ...(PRE_STEP_NODE_IDS.research ?? []),
  ...(PRE_STEP_NODE_IDS.dossier ?? []),
  ...(PRE_STEP_NODE_IDS.qualify ?? []),
  'status.enrolled',
];

/** Whether a contact resting on this node is still in the automatic front-half (before any authored step). */
export function isPrepNode(nodeId: string): boolean {
  return PREP_NODE_IDS.includes(nodeId);
}

/**
 * How the pickable touch/task steps are grouped in the "Add a step" palette — each an optional section
 * header + its ordered step types, rendered as divided sections (see {@link SequenceStepPicker}). The
 * parallel containers (`group` / `spawn`) live in their own section, driven by SEQUENCE_CONTAINER_META.
 * Every {@link SequenceStepType} must appear in exactly one group.
 */
export const SEQUENCE_STEP_GROUPS: { label: string | null; types: SequenceStepType[] }[] = [
  { label: null, types: ['like', 'follow', 'connect', 'message', 'inmail', 'email'] },
  { label: 'Tasks', types: ['call', 'reminder'] },
  { label: 'Timing', types: ['wait', 'await_connect'] },
];

export const STEP_META: Record<SequenceAnyStepType, { label: string; icon: React.JSX.Element }> = {
  like: { label: 'Like', icon: <General.Heart /> },
  follow: { label: 'Follow', icon: <General.Plus /> },
  wait: { label: 'Wait', icon: <Time.Clock /> },
  connect: { label: 'Connect', icon: <Users.UserPlus01 /> },
  await_connect: { label: 'Await accept', icon: <Time.ClockRefresh /> },
  message: { label: 'Message', icon: <General.StepMessage /> },
  inmail: { label: 'InMail', icon: <Communication.InMail04 /> },
  email: { label: 'Email', icon: <Communication.Mail01 /> },
  call: { label: 'Call', icon: <Media.Phone01 /> },
  reminder: { label: 'Reminder', icon: <Alerts.Bell01 /> },
  withdraw: { label: 'Withdraw invite', icon: <General.XCircle /> },
  group: { label: 'Parallel group', icon: <Development.Dataflow04 /> },
  spawn: { label: 'Spawn branch', icon: <Development.GitBranch01 /> },
  switch: { label: 'Switch', icon: <Development.Dataflow03 /> },
};

/** The two container types offered in the palette's "Parallel" section, with picker copy. */
export const SEQUENCE_CONTAINER_META: Record<
  SequenceContainerStepType,
  { label: string; hint: string; icon: React.JSX.Element }
> = {
  group: {
    label: 'Parallel group',
    hint: 'Run several branches at once; continue when all finish.',
    icon: <Development.Dataflow04 />,
  },
  spawn: {
    label: 'Spawn branch',
    hint: 'Start a branch alongside the main line; the main line keeps going.',
    icon: <Development.GitBranch01 />,
  },
  switch: {
    label: 'Switch',
    hint: 'Route the contact by age or ~AUM into one branch (each branch is its own path).',
    icon: <Development.Dataflow03 />,
  },
};
